import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface TeacherGiftPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: {
    id: string;
    username: string;
  };
  teacherId: string;
  onSuccess: () => void;
}

export default function TeacherGiftPointsModal({
  isOpen,
  onClose,
  student,
  teacherId,
  onSuccess,
}: TeacherGiftPointsModalProps) {
  const [points, setPoints] = useState<number>(1);
  const [remark, setRemark] = useState<string>('');
  const [teacherBalance, setTeacherBalance] = useState<number>(0);
  const [todayGifted, setTodayGifted] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // 加载教师余额和今日已赠送数量
  useEffect(() => {
    if (isOpen && teacherId && student.id) {
      loadData();
    }
  }, [isOpen, teacherId, student.id]);

  const loadData = async () => {
    try {
      // 获取教师余额
      const { data: teacherAccount } = await supabase
        .from('user_point_accounts')
        .select('total_points')
        .eq('user_id', teacherId)
        .eq('role', 'teacher')
        .maybeSingle();

      setTeacherBalance(teacherAccount?.total_points || 0);

      // 获取今日已赠送数量
      const today = getBeijingDate();
      const { data: todayGifts } = await supabase
        .from('teacher_daily_gifts')
        .select('points')
        .eq('teacher_id', teacherId)
        .eq('student_id', student.id)
        .eq('gift_date', today);

      const total = todayGifts?.reduce((sum: number, g: any) => sum + g.points, 0) || 0;
      setTodayGifted(total);
    } catch (err) {
      console.error('加载数据失败:', err);
    }
  };

  const getBeijingDate = (): string => {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + 8 * 60 * 60 * 1000);
    const year = beijingTime.getUTCFullYear();
    const month = String(beijingTime.getUTCMonth() + 1).padStart(2, '0');
    const day = String(beijingTime.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const maxCanGift = Math.min(10 - todayGifted, teacherBalance);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // 验证输入
    if (points <= 0 || !Number.isInteger(points)) {
      setError('请输入有效的正整数');
      return;
    }

    if (points > teacherBalance) {
      setError(`U币余额不足，当前余额：${teacherBalance}`);
      return;
    }

    if (todayGifted + points > 10) {
      setError(`今日对该学员已赠送${todayGifted}U币，最多还能赠送${10 - todayGifted}U币`);
      return;
    }

    setLoading(true);

    try {
      // 获取当前会话的 access token
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('未登录');
      }

      // 调用云函数
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          action: 'teacher_gift_points',
          student_id: student.id,
          points,
          remark: remark.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || '赠送失败');
      }

      if (result.success) {
        setSuccess(`成功赠送 ${points} U币给 ${student.username}`);
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        throw new Error(result.message || '赠送失败');
      }
    } catch (err: any) {
      setError(err.message || '赠送失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                奖励U币 - {student.username}
              </h3>
              <button
                onClick={handleClose}
                disabled={loading}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5 space-y-4">
              {/* Info Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="text-xs text-blue-600 mb-1">您的U币余额</div>
                  <div className="text-xl font-bold text-blue-700">{teacherBalance}</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                  <div className="text-xs text-orange-600 mb-1">今日已赠送</div>
                  <div className="text-xl font-bold text-orange-700">{todayGifted} U币</div>
                </div>
              </div>

              {/* Points Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  赠送数量 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max={maxCanGift}
                  value={points}
                  onChange={(e) => setPoints(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                  placeholder="请输入赠送数量"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  最多可赠送 {maxCanGift} U币
                </p>
              </div>

              {/* Remark Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  备注说明（可选）
                </label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value.slice(0, 100))}
                  maxLength={100}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all resize-none"
                  placeholder="请输入备注说明（最多100字）"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {remark.length}/100
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">{success}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || points <= 0 || points > maxCanGift}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    处理中...
                  </>
                ) : (
                  '确认赠送'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
